/**
 * Appointment Outcome Extension — Phase 1
 * ───────────────────────────────────────
 * Sidecar record that augments the existing AppointmentResult with the
 * structured fields the spec calls for: a richer proposal snapshot,
 * structured objections, a derivable next-step intent, and lifecycle state
 * (including communicationStatus for future suppression of nurture sequences).
 *
 * IMPORTANT — additive only.
 *   • Does NOT modify AppointmentResult (29 callers depend on it).
 *   • Keyed by `appointmentResultId` so the join is explicit.
 *   • Designed to be hard-immutable once written (see AppDataContext setter).
 *
 * Phase 2 will wire the next-step / lifecycle into Universal Sequence,
 * Manager Queue, and Marketing Attribution. Phase 1 is the record + the
 * read-only view only.
 */

// ────────────────────────────────────────────────────────────────────
// Objections — structured replacement for freeform notes
// ────────────────────────────────────────────────────────────────────

export type ObjectionCode =
  | "price"
  | "financing"
  | "timing"
  | "spouse_not_present"
  | "comparing_competitors"
  | "trust"
  | "product_fit"
  | "scope_confusion"
  | "install_concern"
  | "not_decision_maker"
  | "other";

export const OBJECTION_LABELS: Record<ObjectionCode, string> = {
  price: "Price",
  financing: "Financing",
  timing: "Timing",
  spouse_not_present: "Spouse not present",
  comparing_competitors: "Comparing competitors",
  trust: "Trust / brand confidence",
  product_fit: "Product fit",
  scope_confusion: "Scope confusion",
  install_concern: "Install concern",
  not_decision_maker: "Not the decision maker",
  other: "Other",
};

export interface OutcomeObjections {
  primary: ObjectionCode;
  /** Secondary blocker — captures the second-most-cited reason. */
  secondary?: ObjectionCode;
  /** Required when primary === "other". */
  notes?: string;
}

// ────────────────────────────────────────────────────────────────────
// Proposal Snapshot — what was actually presented
// ────────────────────────────────────────────────────────────────────

export type PaymentStructure = "cash" | "financing" | "mixed";

export interface ProposalAddOn {
  label: string;
  amount: number;
}

/**
 * Immutable record of what the rep showed the homeowner. Intentionally
 * compact: full build details remain reachable via Build Integrity's
 * configHash. This stores the *commercial* presentation only.
 */
export interface ProposalSnapshot {
  package: string;                 // e.g. "Premium", "Standard"
  layout: string;                  // e.g. "60in alcove, frameless glass"
  productSummary: string;          // one-line plain-language summary
  basePrice: number;
  discountAmount: number;          // 0 if none
  discountReason?: string;         // e.g. "Same-day close", "Manager promo"
  finalPrice: number;              // basePrice - discountAmount
  paymentStructure: PaymentStructure;
  financingTerms?: string;         // e.g. "120 mo @ 9.99% (GreenSky)"
  addOns: ProposalAddOn[];         // empty array if none
  /** Optional config hash from Build Integrity for cross-reference. */
  configHash?: string;
}

// ────────────────────────────────────────────────────────────────────
// Next Step — derived or selected intent (Phase 2 wires automation)
// ────────────────────────────────────────────────────────────────────

export type FollowUpType =
  | "sales_rep_followup"
  | "manager_call"
  | "pricing_review"
  | "financing_followup"
  | "nurture_sequence"
  | "no_action";

export const FOLLOW_UP_TYPE_LABELS: Record<FollowUpType, string> = {
  sales_rep_followup: "Sales rep follow-up",
  manager_call: "Manager call",
  pricing_review: "Pricing review",
  financing_followup: "Financing follow-up",
  nurture_sequence: "Nurture sequence",
  no_action: "No action",
};

export interface OutcomeNextStep {
  followUpRequired: boolean;
  followUpType: FollowUpType;
  /** ISO date — when the follow-up is due. */
  followUpDueAt?: string;
  managerReviewRequired: boolean;
  /** Set when discount > floor, special pricing, or manager promo applied. */
  discountApprovalFlag: boolean;
}

// ────────────────────────────────────────────────────────────────────
// Lifecycle — keeps opportunity active until explicitly resolved
// ────────────────────────────────────────────────────────────────────

export type CloseReason =
  | "won"
  | "lost"
  | "not_fit"
  | "no_response"
  | "opted_out";

export const CLOSE_REASON_LABELS: Record<CloseReason, string> = {
  won: "Won",
  lost: "Lost",
  not_fit: "Not a fit",
  no_response: "No response",
  opted_out: "Opted out",
};

export type CommunicationStatus = "active" | "paused" | "unsubscribed";

export const COMMUNICATION_STATUS_LABELS: Record<CommunicationStatus, string> = {
  active: "Active",
  paused: "Paused",
  unsubscribed: "Unsubscribed",
};

export interface OutcomeLifecycle {
  isClosed: boolean;
  closeReason?: CloseReason;
  communicationStatus: CommunicationStatus;
}

// ────────────────────────────────────────────────────────────────────
// The extension record itself
// ────────────────────────────────────────────────────────────────────

export interface AppointmentOutcomeExtension {
  /** FK to AppointmentResult.id — one extension per result. */
  appointmentResultId: string;
  proposal?: ProposalSnapshot;       // optional: not all results have a proposal (no_demo)
  objections?: OutcomeObjections;    // optional: only for not_sold / no_decision / follow_up
  nextStep: OutcomeNextStep;
  lifecycle: OutcomeLifecycle;
  /** Immutable creation timestamp. */
  capturedAt: string;
  capturedBy: string;
}
